import { useState } from "react";
import { AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { fmtCurrency, fmtDate } from "@/lib/format";
import { paymentLabel } from "@/components/sales/PaymentFields";
import { Panel, SectionLabel } from "@/components/common/ui-kit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const CHECK = {
  not_found: ["Numero non trovato in Fatture in Cloud: controlla numero e data della fattura.", "border-amber-200 bg-amber-50 text-amber-800"],
  error: ["Fatture in Cloud non raggiungibile: verifica non eseguita.", "border-slate-200 bg-slate-50 text-slate-600"],
};

const FicCheck = ({ o, kind, b, onDone }) => {
  const [busy, setBusy] = useState(false);
  const c = CHECK[b.fic_check?.status];
  if (!c) { return null; }
  const again = async () => {
    setBusy(true);
    try { const r = (await api.post(`/orders/${o.id}/billing/${kind}/recheck`)).data; if (!CHECK[r.status]) { toast.success("Fattura trovata e collegata"); } else { toast.warning(CHECK[r.status][0]); } onDone(); }
    catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };
  return (
    <div className={`mt-2 flex flex-wrap items-center gap-2 rounded-md border px-3 py-2 text-xs ${c[1]}`} data-testid={`order-billing-${kind}-fic-warning`}>
      <AlertTriangle className="h-3.5 w-3.5 shrink-0" /><span className="flex-1">{c[0]}</span>
      <Button size="sm" variant="outline" className="h-7 bg-white px-2 text-xs" disabled={busy} onClick={again} data-testid={`order-billing-${kind}-recheck`}>{busy ? "Controllo…" : "Ricontrolla"}</Button>
    </div>
  );
};

const Row = ({ o, kind, label, amount, canEdit, onDone }) => {
  const b = (o.billing || {})[kind] || {};
  const [f, setF] = useState(null);
  const save = async (status) => {
    try {
      const r = (await api.post(`/orders/${o.id}/billing`, { kind, status, invoice_number: f?.number || null, invoice_date: f?.date || null })).data;
      const chk = r?.fic_check?.status;
      if (status === "fatturato" && CHECK[chk]) { toast.warning(`Fattura registrata. ${CHECK[chk][0]}`, { duration: 9000 }); } else { toast.success(status === "fatturato" ? "Fattura registrata" : "Riportato a da fatturare"); }
      setF(null); onDone();
    } catch (e) { toast.error(errorMessage(e)); }
  };
  const done = b.status === "fatturato";
  return (
    <div className="border-t border-line py-3 first:border-0" data-testid={`order-billing-${kind}`}>
      <div className="flex items-center justify-between gap-2 text-sm">
        <span><b className="text-ink">{label}</b> · {fmtCurrency(amount)} <span className="text-xs text-slate-400">+ IVA</span></span>
        <span className={`rounded-full px-2 py-0.5 text-xs ${done ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`} data-testid={`order-billing-${kind}-status`}>{done ? "Fatturato" : "Da fatturare"}</span>
      </div>
      {done && <p className="mt-1 text-xs text-slate-500">Fattura {b.invoice_number} del {fmtDate(b.invoice_date)}</p>}
      {done && <FicCheck o={o} kind={kind} b={b} onDone={onDone} />}
      {canEdit && !done && !f && <Button size="sm" variant="outline" className="mt-2" onClick={() => setF({ number: "", date: new Date().toISOString().slice(0, 10) })} data-testid={`order-billing-${kind}-open`}>Registra fattura</Button>}
      {canEdit && !done && f && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <Input className="h-8 w-36" placeholder="N. fattura" value={f.number} maxLength={60} onChange={(e) => setF({ ...f, number: e.target.value })} data-testid={`order-billing-${kind}-number`} />
          <Input className="h-8 w-40" type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid={`order-billing-${kind}-date`} />
          <Button size="sm" disabled={!f.number.trim()} onClick={() => save("fatturato")} data-testid={`order-billing-${kind}-save`}>Salva</Button>
          <Button size="sm" variant="ghost" onClick={() => setF(null)}>Annulla</Button>
        </div>
      )}
      {canEdit && done && <Button size="sm" variant="ghost" className="mt-1 h-7 px-2 text-xs" onClick={() => save("da_fatturare")} data-testid={`order-billing-${kind}-revert`}>Riporta a da fatturare</Button>}
    </div>
  );
};

export const OrderBillingPanel = ({ o, canEdit, onDone }) => {
  const net = o.taxable || 0;
  const dep = o.deposit_pct ? Math.round(net * o.deposit_pct) / 100 : 0;
  const active = ["confermato", "in_evasione", "parzialmente_evaso", "evaso"].includes(o.status);
  return (
    <Panel className="p-5" data-testid="order-billing">
      <SectionLabel>Pagamento e fatturazione</SectionLabel>
      <p className="mt-2 text-sm text-slate-600" data-testid="order-payment-terms">{paymentLabel(o.payment_terms) || "Condizioni di pagamento non indicate"}{o.deposit_pct ? ` · acconto ${o.deposit_pct}%` : ""}</p>
      <div className="mt-2">
        {o.deposit_pct > 0 && <Row o={o} kind="acconto" label={`Acconto ${o.deposit_pct}%`} amount={dep} canEdit={canEdit && active} onDone={onDone} />}
        {o.ddt_amount > 0 && o.billing?.saldo?.status !== "fatturato" && <p className="py-2 text-xs text-slate-500" data-testid="order-billing-via-ddt">Fatturazione tramite DDT: {fmtCurrency(o.ddt_amount)} nei DDT, il saldo qui sotto è solo il residuo non consegnato.</p>}
        <Row o={o} kind="saldo" label={o.ddt_amount > 0 ? "Saldo residuo" : o.deposit_pct ? "Saldo" : "Fattura ordine"}
          amount={net - dep - (o.ddt_amount || 0)} canEdit={canEdit && (o.status === "evaso" || (o.billing?.saldo?.status === "fatturato" && active))} onDone={onDone} />
        {o.status !== "evaso" && o.status !== "annullato" && <p className="mt-1 text-xs text-slate-500">Il saldo si fattura a ordine evaso.</p>}
      </div>
    </Panel>
  );
};
